/* eslint jsx-a11y/anchor-is-valid: 0 */

import React, {useState, useEffect} from "react";
import {
  Alert,
  Button,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  FormSelect,
  FormInput,
  Modal,
  ModalBody,
} from "shards-react";
import { accessCheck } from "../../utils/tools";
import PageTitle from "../../components/common/PageTitle";
import Pagination from "../../components/common/Pagination";
import { getClient } from "../../reducers/clientReducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faUsers, faArrowDown, faArrowUp, faUser } from '@fortawesome/free-solid-svg-icons';

const Users = (props) => {

  const [users, setUsers] = useState([]);
  const [addedUser, setAddedUser] = useState(props.addedUser);
  const [updatedUser, setUpdatedUser] = useState(props.updatedUser);
  const [addingUserFailed, setAddingUserFailed] = useState(props.addingUserFailed);
  const [impersonationInProgress, setImpersonationInProgress] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('nameAsc');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [impersonationID, setImpersonationID] = useState(null);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const impersonationTimeoutRef = React.useRef(null);

  useEffect(() => {
    const updateData = async () => {
      await props.getClients();
      await props.getUsers();
      await props.getLocations();
      await props.clearCitation();
      window.location.reload();
    }
    if (props.impersonated && impersonationInProgress) {
      cleanupImpersonation();
      updateData();
    }
    if (props.impersonationFailed && impersonationInProgress) {
      cleanupImpersonation();
      alert('Impersonation failed. Please check your permissions and try again.');
    }
    if (!props.impersonating && !props.impersonated && !props.impersonationFailed && impersonationInProgress) {
      cleanupImpersonation();
    }
  }, [props.impersonating, props.impersonated, props.impersonationFailed, impersonationInProgress, props]);
  useEffect(() => {
    return () => {
      if (impersonationTimeoutRef.current) {
        clearTimeout(impersonationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    props.getLiveUsers(props.user.id)
    document.title = 'Users: EnforcePlus admin';
    !accessCheck(props.user, 'users') && (window.location.href = '/')
  }, [])

  useEffect(() => {
    setUsers(props.users)
    console.log('Users loaded:', props.users)
    console.log('Current user type:', props.user.type)
  }, [props.users])

  useEffect(() => {
    if (props.addedUser) {
      setAddedUser(true);
      setTimeout(() => {
        setAddedUser(false)
        props.clearAddedUser()
      }, 10000);
    }
    if (props.updatedUser) {
      setUpdatedUser(true);
      setTimeout(() => {
        setUpdatedUser(false)
        props.clearAddedUser()
      }, 10000);
    }
    if (props.addingUserFailed) {
      setAddingUserFailed(true);
      setTimeout(() => {
        setAddingUserFailed(false)
      }, 10000);
    }
  }, [props.addedUser, props.updatedUser, props.addingUserFailed])

  // Reset to page 1 when the search or sort changes
  useEffect(() => {
    setPage(1);
  }, [search, sortBy]);

  const filterUsers = () => {
    let sortedUsers = [...users];
    console.log('Filtering users:', sortedUsers.length, 'users')

    // Active/inactive filter
    if (activeFilter === 'active') {
      sortedUsers = sortedUsers.filter(user => user.active);
    } else if (activeFilter === 'inactive') {
      sortedUsers = sortedUsers.filter(user => !user.active);
    }

    // Role filter
    if (roleFilter !== 'all') {
      sortedUsers = sortedUsers.filter(user => getRole(user) === roleFilter);
    }

    if (search !== '') {
      sortedUsers = sortedUsers.filter(user => (
        (user.name || '').toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        (user.email || '').toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        (getRole(user) || '').toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        (getClient(user) || '').toLowerCase().indexOf(search.toLowerCase()) !== -1
      ))
    }
    
    if (sortBy === 'nameAsc') {
      sortedUsers = sortedUsers.sort((a, b) => a.name?.localeCompare(b.name));
    }
    if (sortBy === 'nameDesc') {
      sortedUsers = sortedUsers.sort((a, b) => b.name?.localeCompare(a.name));
    }
    if (sortBy === 'emailAsc') {
      sortedUsers = sortedUsers.sort((a, b) => a.email?.localeCompare(b.email));
    }
    if (sortBy === 'emailDesc') {
      sortedUsers = sortedUsers.sort((a, b) => b.email?.localeCompare(a.email));
    }
    if (sortBy === 'roleAsc') {
      sortedUsers = sortedUsers.sort((a, b) => getRole(a)?.localeCompare(getRole(b)));
    }
    if (sortBy === 'roleDesc') {
      sortedUsers = sortedUsers.sort((a, b) => getRole(b)?.localeCompare(getRole(a)));
    }
    if (sortBy === 'clientAsc') {
      sortedUsers = sortedUsers.sort((a, b) => getClient(a)?.localeCompare(getClient(b)));
    }
    if (sortBy === 'clientDesc') {
      sortedUsers = sortedUsers.sort((a, b) => getClient(b)?.localeCompare(getClient(a)));
    }
    if (sortBy === 'activeAsc') {
      sortedUsers = sortedUsers.sort((a, b) => String(a.active).localeCompare(String(b.active)));
    }
    if (sortBy === 'activeDesc') {
      sortedUsers = sortedUsers.sort((a, b) => String(b.active).localeCompare(String(a.active)));
    }
    
    console.log('Filtered users:', sortedUsers.length, 'users')
    return sortedUsers
  }

  const getPaginatedUsers = () => {
    const filteredUsers = filterUsers();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredUsers.slice(startIndex, endIndex);
  }

  const getRole = user => {
    switch (user.type) {
      case "super":
        return 'Super Admin'
      case "admin":
        return 'Admin' 
      case "regional":
        return 'Regional Admin'
      case "citation_admin":
        return 'Citation Admin'
      case "officer":
        return 'Enforcement officer'
      default:
        return 'Unknown'
    }
  }

  const getClient = user => {
    let client = props.clients.find(client => client.id === user.client)
    if (client) {
      return client.name
    }  else {
      return 'Internal'
    }
  }

  const cleanupImpersonation = () => {
    setImpersonationInProgress(false);
    setShowImpersonationModal(false);
    setImpersonationID(null);
    if (impersonationTimeoutRef.current) {
      clearTimeout(impersonationTimeoutRef.current);
      impersonationTimeoutRef.current = null;
    }
  };

  const handleImpersonate = async (userId) => {
    if (impersonationInProgress || props.impersonated) return;
    // Find the user's name for display in the modal
    const userToImpersonate = users.find(u => u.id === userId);
    setImpersonationID(userId);
    setShowImpersonationModal(true);
    setImpersonationInProgress(true);
    impersonationTimeoutRef.current = setTimeout(() => {
      console.error('Impersonation timeout - resetting state');
      cleanupImpersonation();
      alert('Impersonation timed out. Please try again.');
    }, 30000); // 30 second timeout
    try {
      await props.impersonate(userId);
    } catch (error) {
      console.error('Impersonation failed:', error);
      cleanupImpersonation();
      alert('Impersonation failed. Please try again.');
    }
  };

  return (
    <Container fluid className="main-content-container px-4">
      <style>
        {`
          .user-row:hover {
            background-color: rgba(33, 170, 115, 0.03);
            cursor: pointer;
          }
          
          .table th {
            border-top: none;
          }
          
          .table td {
            vertical-align: middle;
          }
          .impersonation-modal .modal-content {
            border: none;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            border-radius: 8px;
          }
          .spinner-border {
            color: #21AA73 !important;
          }
        `}
      </style>
      {/* Page Header */}
      
      {addedUser && (
        <Alert className="mb-0 mt-4" theme={'success'}>
          <i className="fa fa-info mx-2"></i> {props.lastUser.name} has been succesfully added!
        </Alert>
      )}
      {updatedUser && (
        <Alert className="mb-0 mt-4" theme={'success'}>
          <i className="fa fa-info mx-2"></i> {props.lastUser.name} has been succesfully updated!
        </Alert>
      )}
      {addingUserFailed && (
        <Alert className="mb-4" theme="danger">
          {props.addingUserFailed}
        </Alert>
      )}

      {/* First Row of Posts */}
      <Row>
        <Col>
          <Row noGutters className="page-header py-4">
            <PageTitle xs="12" sm="4" title="Users" className="text-sm-left mb-3" />
          </Row>
          <Card small className="mb-4" style={{
            borderRadius: '6px', 
            boxShadow: '0 1px 10px rgba(0,0,0,0.05)', 
            border: '1px solid #eaedf3',
            margin: '20px 0'
          }}>
            <CardHeader className="border-bottom">
              <Row>
                <Col xs={8} sm={5} className="d-flex align-items-center">
                  <h6 className="m-0 font-weight-bold d-flex align-items-center">
                    <FontAwesomeIcon icon={faUsers} className="mr-2" style={{color: '#21AA73', opacity: 0.8}} />
                    Users ({filterUsers().length})
                    {props.user.type === 'regional' && (
                      <span className="ml-2" style={{fontSize: '12px', color: '#818ea3', fontWeight: 'normal'}}>
                        (Regional Admins & Officers only)
                      </span>
                    )}
                  </h6>
                  {/* Active/Inactive/All Filter */}
                  <FormSelect
                    value={activeFilter}
                    onChange={e => setActiveFilter(e.target.value)}
                    style={{ width: 140, marginLeft: 16, fontSize: 13 }}
                  >
                    <option value="all">All Users</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                  </FormSelect>
                  {/* Role Filter */}
                  <FormSelect
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value)}
                    style={{ width: 160, marginLeft: 12, fontSize: 13 }}
                  >
                    <option value="all">All Roles</option>
                    <option value="Super Admin">Super Admin</option>
                    <option value="Admin">Admin</option>
                    <option value="Regional Admin">Regional Admin</option>
                    <option value="Citation Admin">Citation Admin</option>
                    <option value="Enforcement officer">Enforcement officer</option>
                    <option value="Unknown">Unknown</option>
                  </FormSelect>
                </Col>
                <Col xs={12} sm={7} className='text-right pl-0 d-flex align-items-center justify-content-end'>
                  <div className="search-container mr-3" style={{ position: 'relative', maxWidth: '100%', flex: '1' }}>
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 32px 8px 36px',
                        fontSize: '14px',
                        border: '1px solid #dfe3e9',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = '#fff';
                        e.target.style.boxShadow = '0 0 0 0.2rem rgba(33, 170, 115, 0.1)';
                        e.target.style.borderColor = 'rgba(33, 170, 115, 0.4)';
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.boxShadow = 'none';
                        e.target.style.borderColor = '#dfe3e9';
                      }}
                    />
                    <FontAwesomeIcon 
                      icon={faSearch} 
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '10px',
                        color: '#818ea3',
                        transition: 'color 0.2s ease'
                      }}
                    />
                    {search && (
                      <FontAwesomeIcon 
                        icon={faTimes}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '10px',
                          color: '#818ea3',
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }}
                        onClick={() => setSearch('')}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#21AA73'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#818ea3'}
                      />
                    )}
                  </div>
                  <Button
                    theme="accent"
                    onClick={() => window.location.href='/add-user'}
                    style={{
                      backgroundColor: '#21AA73',
                      borderColor: '#21AA73',
                      borderRadius: '4px',
                      boxShadow: 'none',
                      fontWeight: '500',
                      padding: '8px 16px',
                    }}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-1" />
                    Add User
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="px-4">
              {filterUsers().length === 0 && (
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={faUsers} size="2x" style={{color: '#21AA73', marginBottom: '10px', opacity: 0.8}} />
                  <h6>No users found</h6>
                  <p style={{fontSize: '12px', color: '#9EA0A5'}}>Try adjusting your search.</p>
                </div>
              )}
              {filterUsers().length > 0 && (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th scope="col" className="border-0" style={{color: '#818ea3', fontWeight: 500, fontSize: 13}}>
                          <div onClick={() => setSortBy(sortBy !== 'nameAsc' ? 'nameAsc' : 'nameDesc')} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                            Name
                            {sortBy === 'nameAsc' && <FontAwesomeIcon icon={faArrowUp} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                            {sortBy === 'nameDesc' && <FontAwesomeIcon icon={faArrowDown} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                          </div>
                        </th>
                        <th scope="col" className="border-0" style={{color: '#818ea3', fontWeight: 500, fontSize: 13}}>
                          <div onClick={() => setSortBy(sortBy !== 'emailAsc' ? 'emailAsc' : 'emailDesc')} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                            Email
                            {sortBy === 'emailAsc' && <FontAwesomeIcon icon={faArrowUp} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                            {sortBy === 'emailDesc' && <FontAwesomeIcon icon={faArrowDown} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                          </div>
                        </th>
                        <th scope="col" className="border-0" style={{color: '#818ea3', fontWeight: 500, fontSize: 13}}>
                          <div onClick={() => setSortBy(sortBy !== 'roleAsc' ? 'roleAsc' : 'roleDesc')} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                            Role
                            {sortBy === 'roleAsc' && <FontAwesomeIcon icon={faArrowUp} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                            {sortBy === 'roleDesc' && <FontAwesomeIcon icon={faArrowDown} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                          </div>
                        </th>
                        <th scope="col" className="border-0" style={{color: '#818ea3', fontWeight: 500, fontSize: 13}}>
                          <div onClick={() => setSortBy(sortBy !== 'activeAsc' ? 'activeAsc' : 'activeDesc')} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                            Active
                            {sortBy === 'activeAsc' && <FontAwesomeIcon icon={faArrowUp} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                            {sortBy === 'activeDesc' && <FontAwesomeIcon icon={faArrowDown} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                          </div>
                        </th>
                        {props.user.type === 'super' && (
                          <th scope="col" className="border-0" style={{color: '#818ea3', fontWeight: 500, fontSize: 13}}>
                            <div onClick={() => setSortBy(sortBy !== 'clientAsc' ? 'clientAsc' : 'clientDesc')} style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                              Client
                              {sortBy === 'clientAsc' && <FontAwesomeIcon icon={faArrowUp} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                              {sortBy === 'clientDesc' && <FontAwesomeIcon icon={faArrowDown} className="ml-1" size="xs" style={{color: '#21AA73'}} />}
                            </div>
                          </th>
                        )}
                        <th scope="col" className="border-0"></th>
                        <th scope="col" className="border-0"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedUsers().map(user => {
                        return (
                          <tr key={user.id} className="user-row" onClick={() => window.location.href = "/edit-user/" + user.id} style={{transition: 'background-color 0.2s ease'}}>
                            <td style={{padding: '12px'}}>
                              <span style={{fontWeight: '500'}}>{user.name}</span>
                            </td>
                            <td style={{padding: '12px'}}>
                              <span style={{color: '#495057'}}>{user.email}</span>
                            </td>
                            <td style={{padding: '12px'}}>
                              <span style={{color: '#495057'}}>{getRole(user)}</span>
                            </td>
                            <td style={{padding: '12px'}}>
                              <span style={{
                                color: user.active ? '#21AA73' : '#dc3545',
                                fontWeight: '500'
                              }}>
                                {user.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            {props.user.type === 'super' && (
                              <td style={{padding: '12px'}}>
                                <span style={{color: '#495057'}}>{getClient(user)}</span>
                              </td>
                            )}
                            <td className="text-right" style={{padding: '12px'}} onClick={(e) => e.stopPropagation()}>
                              {(getRole(user) === 'Admin' || getRole(user) === 'Regional Admin' || getRole(user) === 'Citation Admin' || getRole(user) === 'Enforcement officer') && !props.impersonated && Boolean(user.active) && (
                                props.user.type === 'regional' ? 
                                  // Regional admins can only impersonate other regional admins and enforcement officers
                                  (getRole(user) === 'Regional Admin' || getRole(user) === 'Enforcement officer') && (
                                    <a href="#" 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleImpersonate(user.id);
                                      }}
                                      style={{
                                        color: '#21AA73',
                                        fontWeight: '500',
                                        textDecoration: 'none'
                                      }}
                                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                      {impersonationInProgress && impersonationID === user.id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                          Impersonating...
                                        </>
                                      ) : 'Impersonate'}                              
                                    </a>
                                  )
                                : 
                                  // Super admins and regular admins can impersonate any of the roles the
                                  // outer condition already allows. This used to read
                                  // `getRole(user) === 'Super'`, which getRole never returns (it returns
                                  // 'Super Admin'), so the link never rendered for them at all.
                                  // Checked on the VIEWER's type, not the row's: citation_admin also
                                  // reaches this screen and must not gain impersonation.
                                  (props.user.type === 'super' || props.user.type === 'admin') && (
                                    <a href="#" 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleImpersonate(user.id);
                                      }}
                                      style={{
                                        color: '#21AA73',
                                        fontWeight: '500',
                                        textDecoration: 'none'
                                      }}
                                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                      {impersonationInProgress && impersonationID === user.id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                          Impersonating...
                                        </>
                                      ) : 'Impersonate'}                              
                                    </a>
                                  )
                              )}
                            </td>                           
                            <td className="text-right" style={{padding: '12px'}} onClick={(e) => e.stopPropagation()}>
                              <a 
                                href={"/edit-user/" + user.id}
                                style={{
                                  color: '#21AA73',
                                  fontWeight: '500',
                                  textDecoration: 'none'
                                }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                              >
                                Edit
                              </a>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
          {filterUsers().length > 0 && (
            <Pagination
              data={filterUsers()}
              pageSize={pageSize}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
            />
          )}
        </Col>
      </Row>

      {/* Impersonation Loading Modal */}
      <Modal
        open={showImpersonationModal}
        toggle={() => {}}
        centered
        backdrop="static"
        size="md"
        className="impersonation-modal"
      >
        <ModalBody className="text-center py-5">
          <div className="mb-4">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="sr-only">Impersonating...</span>
            </div>
          </div>
          <h4 className="mb-2">Impersonation in Progress</h4>
          {impersonationID && (() => {
            const userToImpersonate = users.find(u => u.id === impersonationID);
            return userToImpersonate ? (
              <p className="text-muted mb-3">
                Setting up impersonation session for <strong>{userToImpersonate.name}</strong>...
              </p>
            ) : (
              <p className="text-muted mb-3">
                Please wait while we set up your impersonation session...
              </p>
            );
          })()}
          <p className="small text-muted mt-4 mb-4">
            You will be automatically redirected when the process is complete
          </p>
          <Button 
             theme="outline-secondary" 
             size="sm"
             onClick={cleanupImpersonation}
           >
             Cancel
           </Button>
        </ModalBody>
      </Modal>
    </Container>
  );
}

export default Users;
